<template>
  <v-autocomplete
    v-if="permissionFleets.length > 0"
    :value="selectedFleet"
    :items="permissionFleets"
    item-text="title"
    item-value="id"
    return-object
    dense
    solo
    outlined
    flat
    auto-select-first
    hide-details
    autofocus
    @change="onFleetChange"
    :loading="isLoading"
  >
    <template v-slot:item="{ item }">
      <v-list-item-content>
        <v-list-item-title>{{ item.title }}</v-list-item-title>
        <v-list-item-subtitle>{{ item.id }}</v-list-item-subtitle>
      </v-list-item-content>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import { handleError } from "@/lib/utility/handleError";
import { MrfiktivFleetViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { AcknowledgedEventFleetModule } from "@/store/modules/fleet/acknowledged-event.fleet.store";
import { AllEventFleetModule } from "@/store/modules/fleet/all-event.fleet.store";
import { ContractFleetModule } from "@/store/modules/fleet/contract-fleet.store";
import { DamageFleetModule } from "@/store/modules/fleet/damage-fleet.store";
import { InvoiceFleetModule } from "@/store/modules/fleet/invoice-fleet.store";
import { FleetManagerModule } from "@/store/modules/fleet/manager-fleet.store";
import { OverdueEventFleetModule } from "@/store/modules/fleet/overdue-event.fleet.store";
import { VehicleFleetModule } from "@/store/modules/fleet/vehicle-fleet.store";
import { UserModule } from "@/store/modules/me-user.store";
import { PartnerModule } from "@/store/modules/partner";
import { ReportModule } from "@/store/modules/report.store";
import { Component, Vue } from "vue-property-decorator";

@Component
export default class TheParentFleetSelectorList extends Vue {
  isLoading = false;

  get permissionFleets(): MrfiktivFleetViewModelGen[] {
    return FleetManagerModule.permissionFleets.filter((f) => f.partnerId === this.partnerId);
  }

  get partnerId() {
    return PartnerModule.partner._id;
  }

  get fleetPartnerPermission() {
    if (ReportModule.partner.id) {
      return UserModule.fleetPermission.filter((p) => ReportModule.partner.id === p.partnerId);
    }

    return UserModule.fleetPermission;
  }

  get selectedFleet(): MrfiktivFleetViewModelGen | null {
    return FleetManagerModule.selectedFleet;
  }

  /**
   * Distinct list of fleetId found in fleet permission for current partner.
   */
  get distinctPartnerFleetIds() {
    return [...new Set(this.fleetPartnerPermission.map((p) => p.fleetId))];
  }

  get fleetIds() {
    return this.distinctPartnerFleetIds;
  }

  async mounted() {
    if (this.partnerId && this.fleetIds.length > 0) {
      this.isLoading = true;
      await FleetManagerModule.loadParentFleets({
        partnerId: this.partnerId,
        fleetIds: this.fleetIds
      }).finally(() => (this.isLoading = false));
    }

    if (!this.selectedFleet) {
      const defaultFleet = this.permissionFleets[0];
      await FleetManagerModule.setSelectedFleetId({
        fleetId: defaultFleet.id,
        partnerId: defaultFleet.partnerId
      });
    }
  }

  async onFleetChange(fleet: MrfiktivFleetViewModelGen) {
    if (!fleet?.id || !fleet.partnerId) return;

    this.isLoading = true;
    try {
      await FleetManagerModule.getFleetTree({
        fleetId: fleet.id,
        partnerId: fleet.partnerId
      });

      this.$emit("fleetChanged", fleet);

      VehicleFleetModule.reset();
      DamageFleetModule.reset();
      InvoiceFleetModule.reset();
      ContractFleetModule.reset();
      AllEventFleetModule.reset();
      AcknowledgedEventFleetModule.reset();
      OverdueEventFleetModule.reset();

      await this.$router.push({ name: "FleetAnalyticsView" });
    } catch (error) {
      handleError(error);
    } finally {
      this.isLoading = false;
    }
  }
}
</script>

<style lang="css" scoped></style>
